<nz-card>
  <!-- Header -->
  <div class="flex justify-between items-center mb-4">
    <h2 class="text-xl font-semibold m-0">{{ 'servicePackages.title' | translate }}</h2>
    <nz-space>
      <button *nzSpaceItem nz-button nzType="default" (click)="viewPurchases()">
        <span nz-icon nzType="shopping-cart" nzTheme="outline"></span>
        {{ 'servicePackages.buttons.purchases' | translate }}
      </button>
      <!--
        beaut-395: gated by ROLE, matching service-packages.routes.ts, not by
        the `service_packages.manage` permission.

        That permission is seeded for Manager ONLY, and "Administrator" is not
        a seeded system role at all — it is custom per organization. So an
        Administrator reached this page (the routes were fixed in web#313) and
        found no buttons on it, which from the user's seat is the same bug.

        Roles and permissions share one ngx-permissions store — auth.state.ts
        loads `[...roles, ...permissions]` — so a role name works here exactly
        as it does in the route guard. Gating page and buttons on the same list
        is what stops them disagreeing again.
      -->
      <ng-template [ngxPermissionsOnly]="['Admin', 'Administrator', 'Manager']">
        <button *nzSpaceItem nz-button nzType="primary" (click)="createPackage()">
          <span nz-icon nzType="plus" nzTheme="outline"></span>
          {{ 'servicePackages.buttons.create' | translate }}
        </button>
      </ng-template>
    </nz-space>
  </div>

  <!-- Filters -->
  <form [formGroup]="filterForm" class="flex flex-wrap gap-3 mb-4 items-end">
    <beautyapp-input
      formControlName="q"
      [placeholder]="'servicePackages.filters.search' | translate"
      prefixIcon="search"
      clearable
      style="width: 220px;"
    />

    <beautyapp-select
      formControlName="isActive"
      [placeholder]="'servicePackages.filters.status' | translate"
      [data]="activeOptions()"
      displayMember="label"
      valueMember="value"
      clearable
      style="width: 160px;"
    />

    <button nz-button nzType="default" (click)="clearFilters()">
      <span nz-icon nzType="clear" nzTheme="outline"></span>
      {{ 'buttons.clear' | translate }}
    </button>
  </form>

  <!-- Table -->
  <nz-table
    #packageTable
    [nzData]="packages()"
    [nzLoading]="loading()"
    [nzTotal]="total()"
    [nzPageSize]="pageSize()"
    [nzPageIndex]="page()"
    [nzFrontPagination]="false"
    nzShowSizeChanger
    [nzPageSizeOptions]="[10, 20, 50, 100]"
    (nzQueryParams)="onQueryParamsChange($event)"
    nzSize="middle"
    [nzScroll]="{ x: '1000px' }"
  >
    <thead>
      <tr>
        <th>{{ 'servicePackages.columns.name' | translate }}</th>
        <th>{{ 'servicePackages.columns.price' | translate }}</th>
        <th>{{ 'servicePackages.columns.validity' | translate }}</th>
        <th>{{ 'servicePackages.columns.services' | translate }}</th>
        <th>{{ 'servicePackages.columns.status' | translate }}</th>
        <th nzWidth="180px" nzRight>{{ 'servicePackages.columns.actions' | translate }}</th>
      </tr>
    </thead>
    <tbody>
      @for (pkg of packages(); track pkg.id) {
        <tr>
          <!-- Name -->
          <td>
            <div class="font-medium">{{ pkg.name }}</div>
            @if (pkg.description) {
              <div class="text-xs text-gray-500 truncate max-w-xs">{{ pkg.description }}</div>
            }
          </td>

          <!-- Price -->
          <td>
            <span class="font-medium whitespace-nowrap">{{ pkg.price | number:'1.2-2' }} ₾</span>
          </td>

          <!-- Validity -->
          <td>
            {{ pkg.validityDays }} {{ 'servicePackages.days' | translate }}
          </td>

          <!-- Services Count -->
          <td>
            <nz-tag nzColor="blue">
              {{ getItemsCount(pkg) }} {{ 'servicePackages.servicesCount' | translate }}
            </nz-tag>
          </td>

          <!-- Status -->
          <td>
            @if (pkg.isActive) {
              <nz-tag nzColor="green">{{ 'servicePackages.active' | translate }}</nz-tag>
            } @else {
              <nz-tag nzColor="default">{{ 'servicePackages.inactive' | translate }}</nz-tag>
            }
          </td>

          <!-- Actions -->
          <td nzRight>
            <nz-space>
              <button
                *nzSpaceItem
                nz-button
                nzType="text"
                nzSize="small"
                nz-tooltip
                [nzTooltipTitle]="'buttons.view' | translate"
                (click)="viewPackage(pkg)"
              >
                <span nz-icon nzType="eye" nzTheme="outline"></span>
              </button>

              <ng-template [ngxPermissionsOnly]="['Admin', 'Administrator', 'Manager']">
                <button
                  *nzSpaceItem
                  nz-button
                  nzType="text"
                  nzSize="small"
                  nz-tooltip
                  [nzTooltipTitle]="'buttons.edit' | translate"
                  (click)="editPackage(pkg)"
                >
                  <span nz-icon nzType="edit" nzTheme="outline"></span>
                </button>
              </ng-template>

              @if (pkg.isActive) {
                <ng-template [ngxPermissionsOnly]="['Admin', 'Administrator', 'Manager']">
                  <button
                    *nzSpaceItem
                    nz-button
                    nzType="text"
                    nzSize="small"
                    nz-tooltip
                    [nzTooltipTitle]="pkg.items?.length
                      ? ('servicePackages.buttons.sell' | translate)
                      : 'პაკეტის გაყიდვა სერვისების გარეშე შეუძლებელია'"
                    [disabled]="!pkg.items?.length"
                    (click)="sellPackage(pkg)"
                  >
                    <span nz-icon nzType="shopping-cart" nzTheme="outline" class="text-green-600"></span>
                  </button>
                </ng-template>
              }

              <ng-template [ngxPermissionsOnly]="['Admin', 'Administrator', 'Manager']">
                <button
                  *nzSpaceItem
                  nz-button
                  nzType="text"
                  nzSize="small"
                  nzDanger
                  nz-popconfirm
                  [nzPopconfirmTitle]="'servicePackages.confirm.delete' | translate"
                  (nzOnConfirm)="deletePackage(pkg)"
                  nz-tooltip
                  [nzTooltipTitle]="'buttons.delete' | translate"
                >
                  <span nz-icon nzType="delete" nzTheme="outline"></span>
                </button>
              </ng-template>
            </nz-space>
          </td>
        </tr>
      }
    </tbody>
  </nz-table>
</nz-card>
